/**
 * Fiches « Séance Circulation » — séances types conformes au livret REMC catégorie A
 *
 * Couverture initiale : 8 fiches C2 (circulation normale).
 * À étendre vers C3 (conditions difficiles) puis C4 (autonomie).
 *
 * Format identique à FICHES_PLATEAU pour rendu direct via <PedagogicalCanvas>
 * (trame Conduite : 4 phases / 9 étapes).
 *
 * Source : Livret d'apprentissage REMC 2014 — Catégorie A (motocyclette).
 */

export const FICHES_CIRCULATION = [
  {
    slug: 'connaitre-respecter-regles',
    competence: 'C2-a',
    competenceCat: 'C2',
    competenceLabel: 'Appréhender la route en conditions normales',
    title: 'Connaître et respecter les règles de circulation',
    kicker: 'C2-a · Circulation · 1h · Individuel',
    accent: '#7c3aed',
    icon: 'BookCheck',
    duree: '1h',
    summary:
      "Application stricte du Code de la route en circulation urbaine : limitations, priorités, distances de sécurité, signalisation.",
    meta: {
      duree: '1 heure',
      niveau: 'C2-a · Appréhender la route (REMC)',
      lieu: 'Circulation urbaine + faubourgs',
      materiel: 'Casque-radio · gilet HV · véhicule suiveur formateur',
    },
    steps: {
      evalStatique: {
        content: 'Avant de monter en moto :',
        items: [
          "« Cite-moi les 3 limitations de vitesse principales (50/80/110-130). »",
          "« Quelle est la règle des 2 secondes ? »",
          "Vérifier que toutes les compétences C1 sont validées dans le livret.",
        ],
      },
      evalDynamique: {
        content: 'Sortie 5 min suiveur — observer :',
        items: [
          "Respect des limitations sur les différents axes",
          "Application des priorités à droite / panneaux / feux",
          "Maintien de la distance des 2 secondes derrière véhicule",
          "Détection d'éventuelles infractions involontaires",
        ],
      },
      objectif: {
        content:
          "« À la fin, tu sauras <strong>appliquer en circulation les règles essentielles du Code</strong> (vitesse, priorités, distances) sans aucune infraction observée sur 20 min. »",
        notes: 'Référence livret REMC : C2-a — Connaître et respecter les règles de circulation.',
      },
      criteres: {
        content: "Tu réussis si :",
        items: [
          "Aucun dépassement de vitesse > 5 km/h",
          "Respect strict des STOP et Cédez le passage (arrêt pieds posés au STOP)",
          "Distance ≥ 2 secondes maintenue derrière les véhicules",
          "0 infraction observée sur le trajet de 20 min",
        ],
      },
      cours: {
        content: '10 min en pré-briefing au tableau :',
        items: [
          "Schéma : 4 régimes de priorité (à droite / STOP / Cédez / feu)",
          "Limitations par type d'axe : 50 ville / 80 hors agglo / 110 voie rapide / 130 autoroute",
          "Règle des 2 secondes : du panneau au panneau au passage de l'autre",
        ],
      },
      demonstration: {
        content: 'Conduite commentée formateur (15 min) :',
        items: [
          "Le formateur précède et verbalise par radio chaque application",
          "« Ici je suis à 48 dans le 50 — j'anticipe la sortie d'école »",
          "Mise en évidence des panneaux annoncés à l'avance",
        ],
      },
      guidage: {
        content: "Conduite commentée élève (25 min) — guidage dégressif :",
        items: [
          "<strong>FORT</strong> : élève verbalise chaque panneau et chaque décision",
          "<strong>MOYEN</strong> : formateur reformule en cas d'oubli",
          "<strong>FAIBLE</strong> : silence radio, observation pure",
        ],
      },
      autonomie: {
        content: 'Boucle 20 min sans intervention :',
        items: [
          "0 rappel verbal sur 20 min consécutives",
          "Le formateur note silencieusement les infractions",
          "Si 0 infraction → validable",
        ],
      },
      bilan: {
        content: "Tu parles EN PREMIER :",
        items: [
          "« Sur 10, à quel point tu te sens en règle ? »",
          "« Quelle infraction tu as le plus de mal à éviter ? »",
          "Moniteur : 2 réussites + 1 axe + prochaine = C2-b (indices).",
        ],
        notes: "Phrase-clé : « Le Code, c'est ma carte d'identité de motard. »",
      },
    },
  },

  {
    slug: 'rechercher-signalisation-indices',
    competence: 'C2-b',
    competenceCat: 'C2',
    competenceLabel: 'Appréhender la route en conditions normales',
    title: 'Rechercher la signalisation et les indices utiles',
    kicker: 'C2-b · Circulation · 1h · Individuel',
    accent: '#7c3aed',
    icon: 'Eye',
    duree: '1h',
    summary:
      "Balayage visuel ample, lecture anticipée de la signalisation et identification des indices spécifiques 2RM (revêtement, mobilier, comportements).",
    meta: {
      duree: '1 heure',
      niveau: 'C2-b · Appréhender la route (REMC)',
      lieu: 'Circulation mixte (ville + faubourg)',
      materiel: 'Casque-radio · véhicule suiveur',
    },
    steps: {
      evalStatique: {
        content: 'Avant de partir :',
        items: [
          "« Cite-moi 3 indices spécifiques au 2-roues (revêtement, mobilier, autres usagers). »",
          "« À quelle fréquence tu regardes tes rétros ? »",
          'Vérifier C2-a (règles) validé.',
        ],
      },
      evalDynamique: {
        content: 'Sortie libre 10 min — observer :',
        items: [
          "Fréquence du regard loin vs proche",
          "Réaction aux panneaux : adaptation visible ou surprise ?",
          "Détection d'indices typiques (gravier, plaque égout, portière)",
        ],
      },
      objectif: {
        content:
          "« À la fin, tu sauras <strong>balayer visuellement loin + proche + rétros toutes les 5-7 secondes</strong> et identifier au moins 3 indices spécifiques 2RM par minute. »",
        notes: "Référence livret REMC : C2-b. Phrase-clé : « Ce que je ne vois pas peut me blesser. »",
      },
      criteres: {
        content: 'Critères :',
        items: [
          "Balayage visuel ample (loin + proche + rétros) toutes les 5-7 sec",
          "Lecture anticipée des panneaux (pas de surprise)",
          "3+ indices 2RM verbalisés par minute",
          "Adaptation visible de la conduite aux indices détectés",
        ],
      },
      cours: {
        content: '10 min pré-briefing :',
        items: [
          "Schéma : champ visuel motard vs auto (asymétrie, casque)",
          "Indices typiques 2RM : revêtement (gravier, marquage), mobilier (poteaux, glissières), comportements (portières, distraction)",
          "Méthode S.O.R.A.D. : Scanner → Observer → Reconnaître → Anticiper → Décider",
        ],
      },
      demonstration: {
        content: 'Conduite commentée formateur (15 min) :',
        items: [
          "Verbalisation systématique : « Là un STOP dans 50 m — je décélère »",
          "Énumération des indices : « Plaque mouillée à 11h, je dévie »",
          "Démo regard 360° avant chaque manœuvre",
        ],
      },
      guidage: {
        content: 'Conduite élève (25 min) :',
        items: [
          "<strong>FORT</strong> : élève verbalise CHAQUE indice détecté",
          "<strong>MOYEN</strong> : formateur teste « tu as vu le… ? »",
          "<strong>FAIBLE</strong> : élève autonome, débriefing après",
        ],
      },
      autonomie: {
        content: 'Boucle 15 min :',
        items: [
          "Le formateur note les indices manqués",
          "Si ≤ 2 indices manqués → validable",
        ],
      },
      bilan: {
        content: 'Bilan :',
        items: [
          "« Quel indice tu as le plus de mal à voir ? »",
          "« Que se passerait-il si tu ratais le marquage glissant à 70 km/h ? »",
          "Moniteur : 2 réussites + 1 axe + prochaine = C2-c (placement).",
        ],
      },
    },
  },

  {
    slug: 'positionner-chaussee',
    competence: 'C2-c',
    competenceCat: 'C2',
    competenceLabel: 'Appréhender la route en conditions normales',
    title: 'Positionner le véhicule sur la chaussée',
    kicker: 'C2-c · Circulation · 1h · Individuel',
    accent: '#7c3aed',
    icon: 'AlignCenter',
    duree: '1h',
    summary:
      "Choisir sa position selon le contexte : 1/3 droit hors agglo (visibilité + écart à la voie inverse), centre ou 1/3 gauche en agglo (anti-portières). Sans jamais chevaucher la ligne médiane.",
    meta: {
      duree: '1 heure',
      niveau: 'C2-c · Appréhender la route (REMC)',
      lieu: 'Mix urbain + hors agglomération',
      materiel: 'Casque-radio · véhicule suiveur',
    },
    steps: {
      evalStatique: {
        content: 'Avant de partir :',
        items: [
          "« Où places-tu ta moto sur une départementale ? Et en ville ? »",
          "« Pourquoi la position varie selon le contexte ? »",
          'Vérifier C2-a et C2-b validés.',
        ],
      },
      evalDynamique: {
        content: 'Sortie 10 min — observer :',
        items: [
          "Position dans la voie selon le contexte (hors agglo vs agglo) ?",
          "Tu chevauches la ligne médiane ? (jamais autorisé)",
          "Ajustement en présence de stationnement / camions / virages",
          "Contrôle visuel avant changement de voie",
        ],
      },
      objectif: {
        content:
          "« À la fin, tu sauras <strong>te placer dans le 1/3 droit hors agglomération</strong> et <strong>au centre voire 1/3 gauche en agglomération</strong>, sans jamais toucher la ligne médiane. »",
        notes: 'Référence livret REMC : C2-c. Phrase-clé : « Ma place dépend du contexte — je suis vu, je m\'écarte des dangers. »',
      },
      criteres: {
        content: 'Critères :',
        items: [
          "<strong>Hors agglo</strong> : position dans le 1/3 droit de la voie (écart à la voie inverse)",
          "<strong>Agglo</strong> : position centre / 1/3 gauche (écart aux véhicules garés + portières)",
          "<strong>Jamais</strong> de chevauchement de la ligne médiane (axiale continue ou discontinue)",
          "Ajustement spontané selon obstacles (camion, stationnement, piétons)",
          "Changement de voie : contrôle visuel + clignotant + insertion fluide",
        ],
      },
      cours: {
        content: '10 min pré-briefing :',
        items: [
          "Schéma vu de dessus : voie + 3 tiers (gauche / centre / droite)",
          "<strong>Hors agglo</strong> : 1/3 droit → plus visible des automobilistes en face + écart à la voie inverse + meilleure trajectoire en virage",
          "<strong>Agglo</strong> : centre ou 1/3 gauche → écart aux portières + visibilité dans rétros voitures + anticipation des piétons",
          "<strong>Ligne médiane = INTOUCHABLE</strong> (axe central, axiale) — ni chevauchée ni franchie",
          "En virage : la position varie selon le sens (trajectoire de sécurité)",
        ],
      },
      demonstration: {
        content: 'Conduite commentée formateur (15 min) :',
        items: [
          "Sur la départementale : « Je suis dans le 1/3 droit, écarté de la voie d\'en face »",
          "En ville : « Je passe au centre / 1/3 gauche, j\'évite les portières des stationnements »",
          "Face à un camion : « Je m\'écarte SANS toucher la ligne médiane »",
          "Changement de voie : « contrôle, clignotant, vérif angle mort, j'insère »",
        ],
      },
      guidage: {
        content: 'Conduite élève (25 min) :',
        items: [
          "<strong>FORT</strong> : formateur appelle « droit » (hors agglo) ou « centre/gauche » (agglo) à chaque dérive",
          "<strong>MOYEN</strong> : juste « place » au bon moment + rappel ligne médiane si chevauchement",
          "<strong>FAIBLE</strong> : silence, débriefing après",
        ],
      },
      autonomie: {
        content: 'Boucle 15 min :',
        items: [
          "Position correcte selon contexte 95% du temps",
          "0 chevauchement de ligne médiane",
          "0 changement de voie non sécurisé",
        ],
      },
      bilan: {
        content: 'Bilan :',
        items: [
          "« Tu sens la différence entre 1/3 droit (route) et centre (ville) ? »",
          "« Tu as touché la ligne médiane à un moment ? »",
          "Moniteur : 2 réussites + 1 axe + prochaine = C2-d (allure).",
        ],
      },
    },
  },

  {
    slug: 'adapter-allure',
    competence: 'C2-d',
    competenceCat: 'C2',
    competenceLabel: 'Appréhender la route en conditions normales',
    title: "Adapter l'allure aux situations",
    kicker: 'C2-d · Circulation · 1h · Individuel',
    accent: '#7c3aed',
    icon: 'Gauge',
    duree: '1h',
    summary:
      "Modulation de la vitesse selon le contexte (visibilité, météo, trafic, signalisation), parfois en dessous des limites légales.",
    meta: {
      duree: '1 heure',
      niveau: 'C2-d · Appréhender la route (REMC)',
      lieu: 'Circulation variée (école, hôpital, marché)',
      materiel: 'Casque-radio · véhicule suiveur',
    },
    steps: {
      evalStatique: {
        content: 'Avant de partir :',
        items: [
          "« À 50 km/h en sortie d'école, tu fais quoi ? »",
          "« La limite légale, c'est une cible à atteindre ? »",
          'Vérifier C2-a validé.',
        ],
      },
      evalDynamique: {
        content: 'Sortie 10 min — observer :',
        items: [
          "Adaptation aux zones à risque (école, marché, intersection)",
          "Anticipation de la décélération (lever de gaz vs freinage brusque)",
          "Régime moteur cohérent",
        ],
      },
      objectif: {
        content:
          "« À la fin, tu sauras <strong>moduler ta vitesse selon le contexte</strong> (visibilité, météo, trafic), parfois sous la limite légale. »",
        notes: 'Référence livret REMC : C2-d. Article R413-17 (vitesse inadaptée).',
      },
      criteres: {
        content: 'Critères :',
        items: [
          "Anticipation de la décélération AVANT chaque situation à risque",
          "Vitesse adaptée à la visibilité (ex : 30 km/h sortie d'école)",
          "Reprise progressive sans à-coup",
          "Régime moteur cohérent (pas de sur-régime)",
        ],
      },
      cours: {
        content: '10 min pré-briefing :',
        items: [
          "Article R413-17 : « vitesse inadaptée »  → contravention même sous la limite",
          "Distance d'arrêt = V²/150 (sur sec) — doubler sous la pluie",
          "Méthode : observation visuelle → analyse risque → adaptation vitesse",
        ],
      },
      demonstration: {
        content: 'Conduite commentée formateur (15 min) :',
        items: [
          "« Ici je suis à 35 dans le 50 — visibilité réduite par stationnement »",
          "Anticipation : lever de gaz 50 m avant la zone, pas de freinage brutal",
          "Reprise progressive sortie de zone",
        ],
      },
      guidage: {
        content: 'Conduite élève (25 min) :',
        items: [
          "<strong>FORT</strong> : élève verbalise ses choix de vitesse à voix haute",
          "<strong>MOYEN</strong> : formateur teste « pourquoi tu ralentis ici ? »",
          "<strong>FAIBLE</strong> : silence, débriefing après",
        ],
      },
      autonomie: {
        content: 'Boucle 15 min :',
        items: [
          "0 vitesse inadaptée sur 15 min",
          "Anticipations visibles (lever de gaz avant chaque zone)",
        ],
      },
      bilan: {
        content: 'Bilan :',
        items: [
          "« Quelle situation t'a le plus surpris ? »",
          "« Tu te sens plus stressé(e) ou plus en sécurité quand tu ralentis avant ? »",
          "Moniteur : 2 réussites + 1 axe + prochaine = C2-e (arrêt/stationnement).",
        ],
        notes: "Phrase-clé : « La limite légale n'est pas une obligation d'aller à fond. »",
      },
    },
  },

  {
    slug: 'arret-stationnement-secu',
    competence: 'C2-e',
    competenceCat: 'C2',
    competenceLabel: 'Appréhender la route en conditions normales',
    title: "S'arrêter, stationner et repartir en sécurité",
    kicker: 'C2-e · Circulation · 45 min · Individuel',
    accent: '#7c3aed',
    icon: 'ParkingCircle',
    duree: '45 min',
    summary:
      "Choix d'un emplacement légal, usage de la béquille adaptée, antivol systématique, redémarrage en sécurité avec contrôle 360°.",
    meta: {
      duree: '45 minutes',
      niveau: 'C2-e · Appréhender la route (REMC)',
      lieu: 'Parcours urbain avec arrêts variés (parking, rue, pente)',
      materiel: 'Antivol · gilet HV',
    },
    steps: {
      evalStatique: {
        content: 'Avant de partir :',
        items: [
          "« Quelles règles de stationnement 2RM connais-tu ? »",
          "« Quel antivol utilises-tu d'habitude ? »",
          'Vérifier C1-c (manœuvre poussette) validé.',
        ],
      },
      evalDynamique: {
        content: '1 arrêt + 1 redémarrage libre — observer :',
        items: [
          "Choix de l'emplacement (légal / gênant ?)",
          "Pose de béquille (équilibre maintenu ?)",
          "Contrôle 360° avant redémarrage ?",
        ],
      },
      objectif: {
        content:
          "« À la fin, tu sauras <strong>t'arrêter, stationner et repartir en sécurité</strong> dans tous les contextes (ville, parking, pente). »",
        notes: 'Référence livret REMC : C2-e. R417-1 et suivants (stationnement 2RM).',
      },
      criteres: {
        content: 'Critères :',
        items: [
          "Emplacement légal, sans gêner piétons ni voitures",
          "Béquille latérale OU centrale utilisée correctement",
          "Antivol systématique (anti-démarrage / chaîne)",
          "Reprise : contrôle 360° + démarrage progressif + vérif flux",
        ],
      },
      cours: {
        content: '10 min pré-briefing :',
        items: [
          "Règles 2RM : trottoir uniquement si signalé OK, pas de gêne piétons (R417-11)",
          "Béquille latérale : sol dur + pente vers la gauche pour stabilité",
          "Béquille centrale : pour parking long ou pente forte",
          "Antivol : minimum 1 (anti-démarrage), idéal 2 (+ chaîne sur roue arrière)",
        ],
      },
      demonstration: {
        content: 'Démo formateur (10 min) :',
        items: [
          "Approche emplacement + clignotant + arrêt progressif",
          "Pose béquille latérale commentée (test stabilité)",
          "Redémarrage : casque + position + 360° + démarrage + insertion",
        ],
      },
      guidage: {
        content: 'Pratique élève (20 min) :',
        items: [
          "5 arrêts / redémarrages dans contextes variés",
          "<strong>FORT</strong> : formateur guide chaque étape",
          "<strong>FAIBLE</strong> : silence, juste correction si besoin",
        ],
      },
      autonomie: {
        content: 'Évaluation :',
        items: [
          "1 arrêt en pente + redémarrage sécurisé sans recul",
          "0 oubli antivol",
          "0 redémarrage sans contrôle 360°",
        ],
      },
      bilan: {
        content: 'Bilan :',
        items: [
          "« Quelle béquille tu préfères et pourquoi ? »",
          "« Que se passerait-il si tu oubliais l'antivol 1 fois ? »",
          "Moniteur : 2 réussites + 1 axe + prochaine = C2-f (tourner).",
        ],
      },
    },
  },

  {
    slug: 'tourner-droite-gauche',
    competence: 'C2-f',
    competenceCat: 'C2',
    competenceLabel: 'Appréhender la route en conditions normales',
    title: 'Tourner à droite et à gauche en sécurité',
    kicker: 'C2-f · Circulation · 45 min · Individuel',
    accent: '#7c3aed',
    icon: 'CornerUpRight',
    duree: '45 min',
    summary:
      "Tourner-à-droite et tourner-à-gauche en respectant priorités, trajectoires et clignotants. Vitesse adaptée 5-15 km/h en virage urbain.",
    meta: {
      duree: '45 minutes',
      niveau: 'C2-f · Appréhender la route (REMC)',
      lieu: 'Carrefours urbains variés',
      materiel: 'Casque-radio · véhicule suiveur',
    },
    steps: {
      evalStatique: {
        content: 'Avant de partir :',
        items: [
          "« À quelle distance tu mets le clignotant avant un virage ? »",
          "« Tourner à gauche : quelles priorités à vérifier ? »",
          'Vérifier C1-h (regard) + C2-c (placement) validés.',
        ],
      },
      evalDynamique: {
        content: '3 virages libres — observer :',
        items: [
          "Clignotant mis à temps ?",
          "Contrôle rétros + angle mort avant virage ?",
          "Trajectoire : empiète-t-elle sur voie opposée ?",
          "Vitesse adaptée ou trop élevée ?",
        ],
      },
      objectif: {
        content:
          "« À la fin, tu sauras <strong>tourner à droite et à gauche en sécurité</strong>, en respectant priorités et trajectoires. »",
        notes: 'Référence livret REMC : C2-f. Phrase-clé : « Avant de tourner, je signale, je contrôle, je vire. »',
      },
      criteres: {
        content: 'Critères :',
        items: [
          "Clignotant activé ≥ 30 m avant carrefour",
          "Contrôle rétros + angle mort systématique",
          "Trajectoire propre, sans empiéter sur voie opposée",
          "Vitesse 5-15 km/h en virage serré urbain",
        ],
      },
      cours: {
        content: '10 min pré-briefing :',
        items: [
          "Schéma : trajectoire idéale virage droite (large) et gauche (sécurisée)",
          "3 temps : signale → contrôle → vire",
          "Priorités spécifiques tourner-à-gauche : véhicules venant face + cyclistes/piétons",
        ],
      },
      demonstration: {
        content: 'Démo formateur (10 min) :',
        items: [
          "1 tourner droite commenté + 1 tourner gauche commenté",
          "Verbalisation : « clignotant 30 m, rétros, angle mort, je vire »",
        ],
      },
      guidage: {
        content: 'Pratique élève (20 min) :',
        items: [
          "Succession de 8-10 virages variés",
          "<strong>FORT</strong> : formateur appelle « clignotant ! » à chaque oubli",
          "<strong>FAIBLE</strong> : silence, débriefing après",
        ],
      },
      autonomie: {
        content: 'Évaluation :',
        items: [
          "5 virages consécutifs sans oubli clignotant",
          "0 trajectoire empiétant sur voie opposée",
        ],
      },
      bilan: {
        content: 'Bilan :',
        items: [
          "« Quel virage tu trouves le plus stressant : droite ou gauche ? »",
          "« Pourquoi le tourner-à-gauche est plus risqué ? »",
          "Moniteur : 2 réussites + 1 axe + prochaine = C2-g (intersections).",
        ],
      },
    },
  },

  {
    slug: 'franchir-intersections',
    competence: 'C2-g',
    competenceCat: 'C2',
    competenceLabel: 'Appréhender la route en conditions normales',
    title: 'Franchir les intersections selon le régime de priorité',
    kicker: 'C2-g · Circulation · 1h · Individuel',
    accent: '#7c3aed',
    icon: 'Crosshair',
    duree: '1h',
    summary:
      "Identification du régime de priorité de chaque intersection (à droite / STOP / Cédez / feu) et franchissement en sécurité.",
    meta: {
      duree: '1 heure',
      niveau: 'C2-g · Appréhender la route (REMC)',
      lieu: 'Circulation urbaine — recherche intersections variées',
      materiel: 'Casque-radio · véhicule suiveur',
    },
    steps: {
      evalStatique: {
        content: 'Avant de partir :',
        items: [
          "« Cite-moi les 4 régimes de priorité. »",
          "« Au STOP, est-ce que je peux ralentir sans m'arrêter complètement ? »",
          'Vérifier C2-a (règles) + C2-b (indices) validés.',
        ],
      },
      evalDynamique: {
        content: '3 intersections libres — observer :',
        items: [
          "Identification correcte du régime (panneau ou marquage) ?",
          "Décélération anticipée ?",
          "Application stricte au STOP : pieds posés ?",
        ],
      },
      objectif: {
        content:
          "« À la fin, tu sauras <strong>identifier le régime de priorité de chaque intersection</strong> et la franchir en sécurité. »",
        notes: 'Référence livret REMC : C2-g. Phrase-clé : « Une intersection, c\'est 4 décisions en 4 secondes. »',
      },
      criteres: {
        content: 'Critères :',
        items: [
          "Identification correcte du régime à 30 m avant",
          "STOP : arrêt complet + pieds posés (et pas seulement ralenti)",
          "Cédez : ralentissement + vérif flux",
          "0 priorité oubliée sur le trajet",
        ],
      },
      cours: {
        content: '10 min pré-briefing :',
        items: [
          "Tableau des 4 régimes + sanctions associées (R415-X)",
          "Repérage des indices : panneau, marquage au sol, perspective",
          "STOP : amende 135 € + 4 points perdus",
        ],
      },
      demonstration: {
        content: 'Conduite commentée formateur (15 min) :',
        items: [
          "Verbalisation à chaque intersection : « STOP — j'arrête pieds posés »",
          "Démo d'un Cédez : décélération + vérification + reprise progressive",
          "Démo intersection à feu rouge",
        ],
      },
      guidage: {
        content: 'Conduite élève (25 min) :',
        items: [
          "<strong>FORT</strong> : élève verbalise chaque intersection",
          "<strong>MOYEN</strong> : formateur teste « quel régime ici ? »",
          "<strong>FAIBLE</strong> : silence, débriefing",
        ],
      },
      autonomie: {
        content: 'Boucle 15 min :',
        items: [
          "8-10 intersections enchaînées sans erreur",
          "0 STOP non-respecté",
        ],
      },
      bilan: {
        content: 'Bilan :',
        items: [
          "« Quel régime tu as eu le plus de mal à identifier ? »",
          "« Pourquoi le STOP-pieds-posés est important légalement ? »",
          "Moniteur : 2 réussites + 1 axe + prochaine = C2-h (ronds-points).",
        ],
      },
    },
  },

  {
    slug: 'franchir-ronds-points',
    competence: 'C2-h',
    competenceCat: 'C2',
    competenceLabel: 'Appréhender la route en conditions normales',
    title: 'Franchir les ronds-points et giratoires',
    kicker: 'C2-h · Circulation · 45 min · Individuel',
    accent: '#7c3aed',
    icon: 'CircleDot',
    duree: '45 min',
    summary:
      "Franchissement de ronds-points (priorité à droite) et giratoires (cédez à l'entrée). Positionnement et clignotant selon la sortie souhaitée.",
    meta: {
      duree: '45 minutes',
      niveau: 'C2-h · Appréhender la route (REMC)',
      lieu: 'Parcours incluant 5-6 giratoires de complexité croissante',
      materiel: 'Casque-radio · véhicule suiveur',
    },
    steps: {
      evalStatique: {
        content: 'Avant de partir :',
        items: [
          "« Quelle différence entre rond-point et giratoire ? »",
          "« Pour la 3e sortie, quelle voie tu prends à l'entrée ? »",
          'Vérifier C2-c (placement) + C2-g (intersections) validés.',
        ],
      },
      evalDynamique: {
        content: '2 giratoires libres — observer :',
        items: [
          "Cédez le passage respecté à l'entrée ?",
          "Voie choisie cohérente avec la sortie ?",
          "Clignotant droit avant la sortie ?",
        ],
      },
      objectif: {
        content:
          "« À la fin, tu sauras <strong>franchir ronds-points et giratoires en sécurité</strong>, en respectant priorités et positionnement. »",
        notes: 'Référence livret REMC : C2-h. R415-9 (rond-point) vs R415-10 (giratoire).',
      },
      criteres: {
        content: 'Critères :',
        items: [
          "Cédez le passage respecté à l'entrée des giratoires",
          "Voie de droite si 1ère sortie, voie de gauche si > 2e sortie",
          "Clignotant droit AVANT la sortie souhaitée",
          "Sortie fluide sans bloquer les autres usagers",
        ],
      },
      cours: {
        content: '10 min pré-briefing :',
        items: [
          "Schéma : rond-point (priorité à droite) vs giratoire (cédez à l'entrée)",
          "Choix de voie : 1ère sortie = droite, sorties suivantes = gauche puis serrer",
          "Le piège : oublier le clignotant droit AVANT la sortie",
        ],
      },
      demonstration: {
        content: 'Démo formateur (10 min) :',
        items: [
          "1 giratoire 1ère sortie commenté + 1 giratoire 3e sortie commenté",
          "Verbalisation : « cédez → voie gauche → clignotant droit avant ma sortie → sortie »",
        ],
      },
      guidage: {
        content: 'Pratique élève (20 min) :',
        items: [
          "6 giratoires différents (1, 2, 3, dernière sortie)",
          "<strong>FORT</strong> : formateur appelle « clignotant droit ! »",
          "<strong>FAIBLE</strong> : silence",
        ],
      },
      autonomie: {
        content: 'Évaluation :',
        items: [
          "5 giratoires consécutifs sans erreur de voie ni d'oubli clignotant",
          "0 blocage d'autre usager",
        ],
      },
      bilan: {
        content: 'Bilan :',
        items: [
          "« Le plus dur : choisir la voie ou mettre le clignotant droit ? »",
          "« Que se passe-t-il si tu restes voie de gauche pour sortir ? »",
          "Moniteur : 2 réussites + 1 axe + prochaine = C3 (conditions difficiles).",
        ],
        notes: "Phrase-clé : « Au giratoire, je cède, je me place, je sors. »",
      },
    },
  },

  /* ══════════ C3 — CONDITIONS DIFFICILES (11 fiches) ══════════ */

  {
    slug: 'distances-securite',
    competence: 'C3-a',
    competenceCat: 'C3',
    competenceLabel: 'Circuler en conditions difficiles',
    title: 'Évaluer et maintenir les distances de sécurité',
    kicker: 'C3-a · Circulation · 45 min · Individuel',
    accent: '#dc2626',
    icon: 'Ruler',
    duree: '45 min',
    summary: "Maîtrise des distances avec véhicule précédent, latérales et angles morts. Règle des 2 secondes, doublée si pluie.",
    meta: { duree: '45 minutes', niveau: 'C3-a · Conditions difficiles (REMC)', lieu: 'Route + voie rapide', materiel: 'Casque-radio · véhicule suiveur' },
    steps: {
      evalStatique: { content: 'Avant de partir :', items: [
        "« Quelle est la règle des 2 secondes ? »",
        "« Sous la pluie, comment tu adaptes ta distance ? »",
        'Vérifier C2-d (allure) validé.',
      ]},
      evalDynamique: { content: '5 min suiveur — observer :', items: [
        "Distance ≥ 2 sec maintenue ou tendance au collage ?",
        "Réaction au freinage brusque du véhicule précédent",
      ]},
      objectif: { content: "« À la fin, tu sauras <strong>maintenir la règle des 2 sec</strong> en sec, doublée sous la pluie (4 sec). »", notes: 'Livret REMC : C3-a. Distance d\'arrêt = V²/150 sec, V²/75 pluie.' },
      criteres: { content: 'Critères :', items: [
        "Distance ≥ 2 sec (4 sec sous pluie) sur 95% du trajet",
        "0 collage en cas de ralentissement",
        "Repli d'urgence (gauche/droite) anticipé",
      ]},
      cours: { content: '10 min pré-briefing :', items: [
        "Calcul : 50 km/h → 28 m d'arrêt sec, 56 m sous pluie",
        "Méthode du point de repère : compter « 21, 22 » au passage du précédent",
        "Latéral : 1 m mini d'un cycliste, 1,5 m hors agglo",
      ]},
      demonstration: { content: 'Démo formateur (10 min) :', items: [
        "Verbalisation : « j'ai 2 sec — je vois encore les rétros du précédent »",
        "Démo collage volontaire → freinage d'urgence simulé",
      ]},
      guidage: { content: 'Conduite élève (15 min) :', items: [
        "<strong>FORT</strong> : formateur compte les sec à voix haute",
        "<strong>FAIBLE</strong> : élève vérifie seul",
      ]},
      autonomie: { content: '10 min :', items: [ "5 ralentissements anticipés sans collage" ]},
      bilan: { content: 'Bilan :', items: [
        "« Te sens-tu en sécurité avec 2 sec ? »",
        "Moniteur : prochaine = C3-b (croiser/dépasser).",
      ]},
    },
  },

  {
    slug: 'croiser-suivre-depasser',
    competence: 'C3-b',
    competenceCat: 'C3',
    competenceLabel: 'Circuler en conditions difficiles',
    title: 'Croiser, suivre, dépasser en sécurité',
    kicker: 'C3-b · Circulation · 1h · Individuel',
    accent: '#dc2626',
    icon: 'ArrowLeftRight',
    duree: '1h',
    summary: "Procédure de dépassement en 5 temps (rétros, angle mort, clignotant, dépassement, retour) et croisement sur route à 2 voies.",
    meta: { duree: '1 heure', niveau: 'C3-b · Conditions difficiles (REMC)', lieu: 'Route départementale 2x1', materiel: 'Casque-radio · véhicule suiveur' },
    steps: {
      evalStatique: { content: 'Avant de partir :', items: [
        "« Cite-moi les 5 temps d'un dépassement. »",
        "« Distance latérale mini de dépassement d'un cycliste ? »",
        'Vérifier C3-a (distances) validé.',
      ]},
      evalDynamique: { content: '1 croisement + 1 dépassement libre — observer :', items: [
        "Contrôle rétros + angle mort avant dépassement ?",
        "Clignotant mis à temps ?",
      ]},
      objectif: { content: "« À la fin, tu sauras <strong>dépasser en 5 temps</strong> et croiser un poids lourd en sécurité. »", notes: 'Livret REMC : C3-b. R414-4 (dépassement).' },
      criteres: { content: 'Critères :', items: [
        "5 temps respectés (rétros / angle mort / clignotant / dépassement / retour)",
        "Distance latérale ≥ 1,5 m (cycliste / piéton)",
        "Croisement PL : décalage vers droite + maintien d'allure",
      ]},
      cours: { content: '10 min pré-briefing :', items: [
        "Schéma 5 temps + sanctions (135 € + 3 pts si non-respect)",
        "Risque PL : aspiration + zone aveugle",
        "Distance latérale légale : 1 m ville / 1,5 m hors agglo",
      ]},
      demonstration: { content: 'Démo formateur (15 min) :', items: [
        "Dépassement commenté : « rétros ok, angle mort ok, clignotant, je dépasse, retour »",
        "Croisement PL commenté : « décalage droite, allure stable »",
      ]},
      guidage: { content: 'Conduite élève (25 min) :', items: [
        "<strong>FORT</strong> : formateur appelle chaque étape",
        "<strong>FAIBLE</strong> : silence",
      ]},
      autonomie: { content: '10 min :', items: [ "3 dépassements sans erreur de procédure" ]},
      bilan: { content: 'Bilan :', items: [
        "« Quel temps tu oublies le plus ? »",
        "Moniteur : prochaine = C3-c (virages).",
      ]},
    },
  },

  {
    slug: 'negocier-virages-route',
    competence: 'C3-c',
    competenceCat: 'C3',
    competenceLabel: 'Circuler en conditions difficiles',
    title: 'Négocier les virages sur route',
    kicker: 'C3-c · Circulation · 1h · Individuel',
    accent: '#dc2626',
    icon: 'Spline',
    duree: '1h',
    summary: "Trajectoire de sécurité (extérieur-intérieur-extérieur), regard porté loin, point de corde, sortie en pleine accélération.",
    meta: { duree: '1 heure', niveau: 'C3-c · Conditions difficiles (REMC)', lieu: 'Route sinueuse (départementale)', materiel: 'Casque-radio · véhicule suiveur' },
    steps: {
      evalStatique: { content: 'Avant de partir :', items: [
        "« Décris la trajectoire de sécurité en virage à gauche. »",
        "« Où mets-tu ton regard avant un virage ? »",
        'Vérifier C1-h (regard) + C1-k (inclinaison) validés.',
      ]},
      evalDynamique: { content: '3 virages libres — observer :', items: [
        "Trajectoire : extérieur-intérieur-extérieur ou « coupée » ?",
        "Regard : sur le bitume devant ou vers la sortie ?",
        "Reprise des gaz : trop tôt / au point de corde / trop tard ?",
      ]},
      objectif: { content: "« À la fin, tu sauras <strong>négocier un virage avec trajectoire de sécurité</strong> + regard porté loin, en adaptant ton allure ET ton inclinaison au rayon. »", notes: 'Livret REMC : C3-c. Ne JAMAIS toucher la ligne médiane (axiale).' },
      criteres: { content: 'Critères :', items: [
        "Trajectoire extérieur-intérieur-extérieur, <strong>sans jamais toucher la ligne médiane</strong>",
        "Allure adaptée — freinage TERMINÉ avant l'entrée (zéro freinage en virage)",
        "Inclinaison adaptée au rayon (plus le virage est serré, plus j'incline)",
        "Regard porté à la sortie du virage avant d'y entrer",
        "Reprise des gaz au point de corde, accélération en sortie",
      ]},
      cours: { content: '15 min pré-briefing :', items: [
        "Schéma trajectoire idéale virage gauche vs virage droite",
        "Méthode « regard loin → point de corde → accélération »",
        "<strong>Triplet vital</strong> : trajectoire + allure + inclinaison — les trois s'adaptent au rayon",
        "<strong>Ligne médiane = INTOUCHABLE</strong> (axiale continue ou discontinue)",
        "Pièges : freiner en virage, sortir large, sous-inclinaison, chevaucher la médiane",
      ]},
      demonstration: { content: 'Démo formateur (15 min) :', items: [
        "1 virage gauche commenté + 1 virage droite commenté",
        "Verbalisation : « regard sortie, point de corde, accélération »",
      ]},
      guidage: { content: 'Conduite élève (15 min) :', items: [
        "Boucle 8-10 virages variés",
        "<strong>FORT</strong> : formateur appelle « regard ! » à chaque virage",
        "<strong>FAIBLE</strong> : silence",
      ]},
      autonomie: { content: '10 min :', items: [
        "5 virages consécutifs sans empiétement",
        "0 freinage en virage",
      ]},
      bilan: { content: 'Bilan :', items: [
        "« Quel type de virage tu trouves le plus difficile ? »",
        "Moniteur : prochaine = C3-d (communication).",
      ]},
    },
  },

  {
    slug: 'communiquer-autres-usagers',
    competence: 'C3-d',
    competenceCat: 'C3',
    competenceLabel: 'Circuler en conditions difficiles',
    title: 'Communiquer avec les autres usagers',
    kicker: 'C3-d · Circulation · 45 min · Individuel',
    accent: '#dc2626',
    icon: 'Megaphone',
    duree: '45 min',
    summary: "Utilisation pertinente des clignotants, klaxon, gestes, regard et positionnement pour exprimer ses intentions.",
    meta: { duree: '45 minutes', niveau: 'C3-d · Conditions difficiles (REMC)', lieu: 'Circulation urbaine + faubourg', materiel: 'Casque-radio · véhicule suiveur' },
    steps: {
      evalStatique: { content: 'Avant de partir :', items: [
        "« Quand utilises-tu le klaxon, et quand est-il interdit ? »",
        "« Combien de m avant un changement tu mets le clignotant ? »",
      ]},
      evalDynamique: { content: '5 min — observer :', items: [
        "Clignotants utilisés à temps ?",
        "Regard / signe de tête face aux autres usagers ?",
      ]},
      objectif: { content: "« À la fin, tu sauras <strong>communiquer tes intentions</strong> via clignotants, gestes, regard. »", notes: 'Livret REMC : C3-d. Klaxon : R416-1 (danger uniquement).' },
      criteres: { content: 'Critères :', items: [
        "Clignotant ≥ 30 m avant tout changement de direction/voie",
        "Klaxon : uniquement signaler un danger (pas pour râler)",
        "Regard franc + signe de tête pour s'assurer d'être vu",
      ]},
      cours: { content: '10 min pré-briefing :', items: [
        "Outils de communication : clignotant, klaxon, regard, geste, positionnement",
        "R416-1 : klaxon interdit hors danger (38 € amende)",
        "Importance pour motards : visibilité réduite → sur-communiquer",
      ]},
      demonstration: { content: 'Démo formateur (10 min) :', items: [
        "Verbalisation : « clignotant 30 m, regard piéton, signe de tête, je passe »",
      ]},
      guidage: { content: 'Conduite élève (15 min) :', items: [
        "<strong>FORT</strong> : formateur appelle « clignotant ! »",
        "<strong>FAIBLE</strong> : silence",
      ]},
      autonomie: { content: '5 min :', items: [ "0 oubli clignotant + 0 klaxon abusif" ]},
      bilan: { content: 'Bilan :', items: [
        "« Te sens-tu vu(e) par les automobilistes ? »",
        "Moniteur : prochaine = C3-e (autres usagers).",
      ]},
    },
  },

  {
    slug: 'connaitre-autres-usagers',
    competence: 'C3-e',
    competenceCat: 'C3',
    competenceLabel: 'Circuler en conditions difficiles',
    title: 'Connaître et respecter les autres usagers',
    kicker: 'C3-e · Circulation · 45 min · Individuel',
    accent: '#dc2626',
    icon: 'Users',
    duree: '45 min',
    summary: "Identification des comportements typiques (piétons, cyclistes, livreurs, voitures, scooters) et adaptation de sa conduite.",
    meta: { duree: '45 minutes', niveau: 'C3-e · Conditions difficiles (REMC)', lieu: 'Circulation urbaine (ZFE / centre-ville)', materiel: 'Casque-radio · véhicule suiveur' },
    steps: {
      evalStatique: { content: 'Avant de partir :', items: [
        "« Cite-moi 3 usagers vulnérables. »",
        "« Quelle distance latérale mini pour dépasser un cycliste ? »",
      ]},
      evalDynamique: { content: '10 min observation :', items: [
        "Adaptation face aux piétons hésitants ?",
        "Anticipation face aux scooters / livreurs ?",
      ]},
      objectif: { content: "« À la fin, tu sauras <strong>reconnaître les comportements types</strong> et adapter ta conduite. »", notes: 'Livret REMC : C3-e.' },
      criteres: { content: 'Critères :', items: [
        "Anticipation piéton hésitant à passage clouté (arrêt complet si doute)",
        "Distance ≥ 1 m cycliste / 1,5 m hors agglo",
        "Vigilance accrue face livreurs / scooters (changements brusques)",
      ]},
      cours: { content: '10 min pré-briefing :', items: [
        "Carte des usagers vulnérables (piéton, cycliste, EDPM, livreur)",
        "Comportements typiques : piéton tête baissée smartphone, cycliste sans clignotant",
        "Distance latérale légale : R414-4 al. 3",
      ]},
      demonstration: { content: 'Démo formateur (10 min) :', items: [
        "Verbalisation : « ce piéton hésite — j'arrête »",
        "Verbalisation : « ce scooter zigzague — j'augmente la distance »",
      ]},
      guidage: { content: 'Conduite élève (15 min) :', items: [
        "Tu identifies verbalement chaque usager vulnérable rencontré",
      ]},
      autonomie: { content: '5 min :', items: [ "0 mise en danger d'autrui" ]},
      bilan: { content: 'Bilan :', items: [
        "« Quel usager tu trouves le plus imprévisible ? »",
        "Moniteur : prochaine = C3-f (PL).",
      ]},
    },
  },

  {
    slug: 'cohabiter-poids-lourds',
    competence: 'C3-f',
    competenceCat: 'C3',
    competenceLabel: 'Circuler en conditions difficiles',
    title: 'Cohabiter avec les poids lourds',
    kicker: 'C3-f · Circulation · 45 min · Individuel',
    accent: '#dc2626',
    icon: 'Truck',
    duree: '45 min',
    summary: "Identification des angles morts d'un poids lourd, distance de sécurité, dépassement et croisement spécifiques.",
    meta: { duree: '45 minutes', niveau: 'C3-f · Conditions difficiles (REMC)', lieu: 'Voie rapide / nationale', materiel: 'Casque-radio · véhicule suiveur' },
    steps: {
      evalStatique: { content: 'Avant de partir :', items: [
        "« Où sont les 4 angles morts d'un PL ? »",
        "« Pourquoi ne jamais doubler un PL par la droite ? »",
      ]},
      evalDynamique: { content: '1 croisement + 1 dépassement PL — observer :', items: [
        "Restes-tu dans l'angle mort du PL ?",
        "Distance latérale au dépassement ?",
      ]},
      objectif: { content: "« À la fin, tu sauras <strong>cohabiter avec les PL en sécurité</strong> (angles morts, dépassement, croisement). »", notes: 'Livret REMC : C3-f. 25% des accidents 2RM-PL liés aux angles morts.' },
      criteres: { content: 'Critères :', items: [
        "0 séjour prolongé dans angle mort PL",
        "Dépassement décidé + rapide (≤ 10 sec dans la zone)",
        "Croisement : décalage droite + allure stable",
      ]},
      cours: { content: '10 min pré-briefing :', items: [
        "Schéma 4 angles morts PL (devant à 5 m, droite arrière, gauche arrière, droit derrière)",
        "Statistique : 25% accidents 2RM-PL = angle mort",
        "Règle d'or : si je ne vois pas les rétros PL, le chauffeur ne me voit pas",
      ]},
      demonstration: { content: 'Démo formateur (10 min) :', items: [
        "Dépassement PL commenté : « rétros, angle mort, clignotant, dépassement RAPIDE »",
      ]},
      guidage: { content: 'Conduite élève (15 min) :', items: [
        "Tu verbalises ta position vs le PL : « je vois ses rétros = ok »",
      ]},
      autonomie: { content: '5 min :', items: [ "Maintien hors angle mort sur tout le parcours" ]},
      bilan: { content: 'Bilan :', items: [
        "« Que se passerait-il si tu restais 1 min en angle mort à 90 km/h ? »",
        "Moniteur : prochaine = C3-g (voie rapide).",
      ]},
    },
  },

  {
    slug: 'inserer-sortir-voie-rapide',
    competence: 'C3-g',
    competenceCat: 'C3',
    competenceLabel: 'Circuler en conditions difficiles',
    title: "S'insérer, circuler et sortir d'une voie rapide",
    kicker: 'C3-g · Voie rapide · 1h · Individuel',
    accent: '#dc2626',
    icon: 'Zap',
    duree: '1h',
    summary: "Insertion à vitesse adaptée, circulation voie de droite, dépassements par la gauche uniquement, sortie anticipée.",
    meta: { duree: '1 heure', niveau: 'C3-g · Conditions difficiles (REMC)', lieu: 'Autoroute ou voie rapide ≥ 90 km/h', materiel: 'Casque-radio · véhicule suiveur · radio prévenu' },
    steps: {
      evalStatique: { content: 'Avant de partir :', items: [
        "« Vitesse moto en limitation auto à 130 ? »",
        "« On peut doubler par la droite sur autoroute ? »",
      ]},
      evalDynamique: { content: '1 insertion + 1 sortie libre — observer :', items: [
        "Vitesse adaptée au flux à l'insertion ?",
        "Reste-t-il en voie de droite quand libre ?",
      ]},
      objectif: { content: "« À la fin, tu sauras <strong>t'insérer, circuler et sortir d'une voie rapide</strong> en sécurité. »", notes: 'Livret REMC : C3-g. R412-9 (voie de droite obligatoire).' },
      criteres: { content: 'Critères :', items: [
        "Insertion à vitesse du flux (90-110 km/h)",
        "Maintien voie de droite quand libre",
        "Dépassement par la gauche uniquement",
        "Sortie anticipée : clignotant 200 m avant",
      ]},
      cours: { content: '15 min pré-briefing :', items: [
        "Limitations 2RM cat A : identiques voiture (130 sec / 110 pluie)",
        "R412-9 : voie de droite + dépassement par la gauche uniquement",
        "BAU = bande d'arrêt d'urgence : interdite hors panne",
      ]},
      demonstration: { content: 'Démo formateur (15 min) :', items: [
        "Insertion commentée : « bretelle, vérification, accélération, je m'insère »",
        "Sortie commentée : « panneau à 1000 m, clignotant à 200 m, décélération » ",
      ]},
      guidage: { content: 'Conduite élève (20 min) :', items: [
        "1 boucle autoroute avec 2 insertions + 2 sorties",
        "<strong>FORT</strong> : formateur guide vitesse + voie",
        "<strong>FAIBLE</strong> : silence",
      ]},
      autonomie: { content: '10 min :', items: [ "Voie droite respectée + sortie anticipée OK" ]},
      bilan: { content: 'Bilan :', items: [
        "« Comment ressens-tu la vitesse à 110 vs 50 ? »",
        "Moniteur : prochaine = C3-h (file dense).",
      ]},
    },
  },

  {
    slug: 'circuler-file-dense',
    competence: 'C3-h',
    competenceCat: 'C3',
    competenceLabel: 'Circuler en conditions difficiles',
    title: 'Circuler en file dense',
    kicker: 'C3-h · Circulation · 45 min · Individuel',
    accent: '#dc2626',
    icon: 'Layers',
    duree: '45 min',
    summary: "Maintien d'une distance adaptée en bouchon, vigilance latérale, anticipation des comportements impatients.",
    meta: { duree: '45 minutes', niveau: 'C3-h · Conditions difficiles (REMC)', lieu: 'Boulevard urbain (heure de pointe)', materiel: 'Casque-radio · véhicule suiveur' },
    steps: {
      evalStatique: { content: 'Avant de partir :', items: [
        "« Distance mini en bouchon à 30 km/h ? »",
        "« Quels comportements impatients tu observes habituellement ? »",
      ]},
      evalDynamique: { content: '5 min en file dense :', items: [
        "Distance ≥ 1 voiture maintenue ?",
        "Couverture du frein anticipée ?",
      ]},
      objectif: { content: "« À la fin, tu sauras <strong>circuler en file dense en sécurité</strong> sans risque de tamponner. »", notes: 'Livret REMC : C3-h.' },
      criteres: { content: 'Critères :', items: [
        "Distance ≥ 1 longueur voiture en bouchon",
        "Frein avant 1 doigt couvert en permanence",
        "0 dépassement par la droite ou interfile non autorisée",
      ]},
      cours: { content: '10 min pré-briefing :', items: [
        "Bouchon = zone à risque (40% des chocs arrière en ville)",
        "Couverture frein : 1 doigt sur frein avant = 0,3 sec gagnées",
        "Comportements à anticiper : freinage brusque, portière, piéton",
      ]},
      demonstration: { content: 'Démo formateur (10 min) :', items: [
        "Démo couverture frein constante + distance maintenue",
      ]},
      guidage: { content: 'Conduite élève (15 min) :', items: [
        "Tu maintiens une couverture frein verbalisée",
      ]},
      autonomie: { content: '5 min :', items: [ "0 freinage brusque sur 5 min" ]},
      bilan: { content: 'Bilan :', items: [
        "« Te sens-tu fatigué(e) après 15 min en file dense ? »",
        "Moniteur : prochaine = C3-i (CIF).",
      ]},
    },
  },

  {
    slug: 'circulation-inter-files',
    competence: 'C3-i',
    competenceCat: 'C3',
    competenceLabel: 'Circuler en conditions difficiles',
    title: 'Circulation inter-files (CIF) — zones autorisées',
    kicker: 'C3-i · Voie rapide · 45 min · Individuel',
    accent: '#dc2626',
    icon: 'ArrowRightLeft',
    duree: '45 min',
    summary: "Expérimentation CIF (régions autorisées) : 50 km/h max, deux files extérieures de gauche uniquement, retour file dès flux > 50.",
    meta: { duree: '45 minutes', niveau: 'C3-i · Conditions difficiles (REMC)', lieu: 'Région autorisée CIF — voie rapide saturée', materiel: 'Casque-radio · véhicule suiveur' },
    steps: {
      evalStatique: { content: 'Avant de partir :', items: [
        "« Quelles régions autorisent la CIF ? »",
        "« Vitesse max et entre quelles files ? »",
      ]},
      evalDynamique: { content: '5 min observation :', items: [
        "T'engages-tu à la bonne vitesse ?",
        "Reste-t-il bien entre les 2 files extérieures gauche ?",
      ]},
      objectif: { content: "« À la fin, tu sauras <strong>pratiquer la CIF</strong> en respectant les règles strictes (régions, vitesse, files). »", notes: 'Livret REMC : C3-i. CIF expérimentée dans 21 départements (à mettre à jour selon arrêté en vigueur).' },
      criteres: { content: 'Critères :', items: [
        "Vitesse ≤ 50 km/h pendant la CIF",
        "Position entre les 2 files extérieures gauche UNIQUEMENT",
        "Retour en file dès flux > 50 km/h",
        "Clignotant + regard 360° avant chaque sortie/entrée file",
      ]},
      cours: { content: '15 min pré-briefing :', items: [
        "Liste régions autorisées (vérifier arrêté préfectoral en cours)",
        "Conditions : axe ≥ 70 km/h, ≥ 2 files dans chaque sens, flux saturé",
        "Vitesse max : 50 km/h. Différentiel max : 30 km/h vs flux",
      ]},
      demonstration: { content: 'Démo formateur (10 min) :', items: [
        "Démo prudente + verbalisation : « je m'insère à 40, je sors dès que ça roule »",
      ]},
      guidage: { content: 'Conduite élève (10 min) :', items: [
        "<strong>FORT</strong> : formateur appelle vitesse + position",
        "<strong>FAIBLE</strong> : silence",
      ]},
      autonomie: { content: '5 min :', items: [ "Vitesse + files respectées sans rappel" ]},
      bilan: { content: 'Bilan :', items: [
        "« Te sens-tu en sécurité ou stressé(e) ? »",
        "Moniteur : prochaine = C3-j (adhérence).",
      ]},
    },
  },

  {
    slug: 'adherence-visibilite-reduites',
    competence: 'C3-j',
    competenceCat: 'C3',
    competenceLabel: 'Circuler en conditions difficiles',
    title: 'Adhérence et visibilité réduites',
    kicker: 'C3-j · Circulation · 1h · Individuel',
    accent: '#dc2626',
    icon: 'CloudRain',
    duree: '1h',
    summary: "Adaptation conduite sous pluie, nuit, brouillard : freinage progressif, distances doublées, éclairage anticipé.",
    meta: { duree: '1 heure', niveau: 'C3-j · Conditions difficiles (REMC)', lieu: 'Conditions réelles dégradées si possible', materiel: 'Tenue pluie complète · feux additionnels' },
    steps: {
      evalStatique: { content: 'Avant de partir :', items: [
        "« Sous la pluie, ta distance d'arrêt fait quoi ? »",
        "« De nuit, ton champ visuel est divisé par combien ? »",
      ]},
      evalDynamique: { content: '5 min observation :', items: [
        "Adaptation freinage / allure / éclairage selon conditions ?",
      ]},
      objectif: { content: "« À la fin, tu sauras <strong>adapter ta conduite</strong> sous pluie, nuit ou brouillard. »", notes: 'Livret REMC : C3-j. Distance d\'arrêt × 2 sous pluie.' },
      criteres: { content: 'Critères :', items: [
        "Vitesse −10 à −20 km/h sous pluie",
        "Distance 4 sec (vs 2 sec sec)",
        "Éclairage maximum dès tombée du jour / brouillard",
        "Évitement des zones glissantes (marquage, gravier, plaques)",
      ]},
      cours: { content: '15 min pré-briefing :', items: [
        "Pluie : adhérence × 0,5 → distance d'arrêt × 2",
        "Nuit : champ visuel ÷ 2, perception couleurs ÷ 3",
        "Brouillard : éclairage AV + AR + feux antibrouillard si équipé",
      ]},
      demonstration: { content: 'Démo formateur (15 min) :', items: [
        "Freinage progressif sur sol mouillé démontré",
        "Verbalisation des zones glissantes (passages cloutés, plaques fonte)",
      ]},
      guidage: { content: 'Conduite élève (20 min) :', items: [
        "Tu verbalises chaque adaptation aux conditions",
      ]},
      autonomie: { content: '5 min :', items: [
        "0 freinage brusque sur sol mouillé",
        "Adaptation visible aux conditions",
      ]},
      bilan: { content: 'Bilan :', items: [
        "« Préfères-tu rouler sous la pluie ou de nuit ? Pourquoi ? »",
        "Moniteur : prochaine = C3-k (tunnels/ponts).",
      ]},
    },
  },

  {
    slug: 'tunnels-ponts',
    competence: 'C3-k',
    competenceCat: 'C3',
    competenceLabel: 'Circuler en conditions difficiles',
    title: 'Tunnels et ponts en sécurité',
    kicker: 'C3-k · Circulation · 30 min · Individuel',
    accent: '#dc2626',
    icon: 'Mountain',
    duree: '30 min',
    summary: "Passage en tunnel (éclairage, distance, calme) et sur pont (vent latéral, joints de dilatation glissants).",
    meta: { duree: '30 minutes', niveau: 'C3-k · Conditions difficiles (REMC)', lieu: 'Tunnel ou pont si dispo', materiel: 'Casque-radio · véhicule suiveur' },
    steps: {
      evalStatique: { content: 'Avant de partir :', items: [
        "« En tunnel, feux allumés ou éteints ? »",
        "« Joint de dilatation pont : glissant ou non ? »",
      ]},
      evalDynamique: { content: 'Passage tunnel/pont — observer :', items: [
        "Éclairage allumé à l'entrée ?",
        "Adaptation au vent latéral sur pont ?",
      ]},
      objectif: { content: "« À la fin, tu sauras <strong>aborder tunnel et pont</strong> en sécurité. »", notes: 'Livret REMC : C3-k. R412-19 (éclairage tunnel).' },
      criteres: { content: 'Critères :', items: [
        "Éclairage AV + AR allumés avant entrée tunnel",
        "Distance × 2 dans tunnel (visibilité réduite)",
        "Anticipation vent latéral sur pont",
        "Ralentissement aux joints de dilatation (glissants)",
      ]},
      cours: { content: '10 min pré-briefing :', items: [
        "Tunnel : éclairage obligatoire R412-19 (135 € si oubli)",
        "Pont : vent latéral imprévisible + joints glissants",
        "Si feu d'urgence en tunnel : se garer à droite, couper moteur",
      ]},
      demonstration: { content: 'Démo formateur (5 min) :', items: [
        "Passage tunnel commenté avec activation éclairage",
      ]},
      guidage: { content: 'Conduite (10 min) :', items: [ "Tu vérifies que ton éclairage est activé avant d'entrer" ]},
      autonomie: { content: '5 min :', items: [ "Procédure tunnel et pont OK" ]},
      bilan: { content: 'Bilan :', items: [
        "Moniteur : C3 complète → passage à C4 (autonomie).",
      ]},
    },
  },

  /* ══════════ C4 — AUTONOMIE (7 fiches) ══════════ */

  {
    slug: 'suivre-itineraire-autonome',
    competence: 'C4-a',
    competenceCat: 'C4',
    competenceLabel: 'Pratiquer une conduite autonome',
    title: 'Suivre un itinéraire de manière autonome',
    kicker: 'C4-a · Autonomie · 1h · Individuel',
    accent: '#16a34a',
    icon: 'Map',
    duree: '1h',
    summary: "Préparation d'itinéraire, lecture carte/GPS, navigation autonome sans rappel formateur.",
    meta: { duree: '1 heure', niveau: 'C4-a · Autonomie (REMC)', lieu: 'Itinéraire défini à l\'avance (15-20 km)', materiel: 'GPS optionnel · roadbook papier' },
    steps: {
      evalStatique: { content: 'Avant de partir :', items: [
        "« Cite-moi 3 méthodes de navigation. »",
        "Préparation itinéraire 10 min sur carte",
      ]},
      evalDynamique: { content: '5 min lancement libre :', items: [
        "Choix de l'itinéraire cohérent ?",
        "Anticipation des intersections clés ?",
      ]},
      objectif: { content: "« À la fin, tu sauras <strong>suivre un itinéraire en autonomie</strong> sans rappel formateur. »", notes: 'Livret REMC : C4-a.' },
      criteres: { content: 'Critères :', items: [
        "0 erreur de navigation sur 15 km",
        "0 rappel formateur",
        "Adaptation aux imprévus (déviation, sens interdit)",
      ]},
      cours: { content: '10 min pré-briefing :', items: [
        "3 méthodes : mémorisation, GPS audio, roadbook fléché",
        "GPS : configuration AVANT départ (interdiction de manipuler en roulant)",
        "Méthode des points de repère",
      ]},
      demonstration: { content: 'Démo formateur (10 min) :', items: [
        "Lecture roadbook + interprétation panneaux",
      ]},
      guidage: { content: 'Conduite élève (25 min) :', items: [
        "Tu navigues + ton formateur silencieux (suiveur)",
      ]},
      autonomie: { content: '10 min :', items: [ "Itinéraire complet sans aide" ]},
      bilan: { content: 'Bilan :', items: [
        "« Te sens-tu autonome maintenant ? »",
        "Moniteur : prochaine = C4-b (longue distance).",
      ]},
    },
  },

  {
    slug: 'longue-distance',
    competence: 'C4-b',
    competenceCat: 'C4',
    competenceLabel: 'Pratiquer une conduite autonome',
    title: 'Préparer et effectuer un voyage longue distance',
    kicker: 'C4-b · Autonomie · 2h · Individuel',
    accent: '#16a34a',
    icon: 'Compass',
    duree: '2h',
    summary: "Préparation voyage (itinéraire, météo, équipement, bagages) + gestion fatigue (pauses 2h, hydratation).",
    meta: { duree: '2 heures', niveau: 'C4-b · Autonomie (REMC)', lieu: 'Trajet 60+ km mixte', materiel: 'Bagages · vêtements météo · trousse premiers secours' },
    steps: {
      evalStatique: { content: 'Avant de partir :', items: [
        "« Cite-moi les 5 vérifications avant un long trajet. »",
        "« Toutes les combien de temps tu prévois une pause ? »",
      ]},
      evalDynamique: { content: 'Préparation + 30 min :', items: [
        "Préparation moto et bagages cohérente ?",
        "Maintien allure + position sans fatigue visible ?",
      ]},
      objectif: { content: "« À la fin, tu sauras <strong>préparer et effectuer un voyage longue distance</strong> en autonomie et sécurité. »", notes: 'Livret REMC : C4-b. Fatigue = 1ère cause d\'accident en longue distance.' },
      criteres: { content: 'Critères :', items: [
        "Préparation : pneus, niveaux, éclairage, météo, bagages équilibrés",
        "Pause obligatoire toutes les 2h ou 200 km",
        "Hydratation toutes les pauses",
        "Adaptation tenue à la météo prévue (couches)",
      ]},
      cours: { content: '20 min pré-briefing :', items: [
        "Check-list pré-voyage : niveaux, pression pneus, éclairage, chaîne, freins",
        "Bagages : poids + équilibrage gauche/droite",
        "Fatigue : signes (bâillements, regard flou, gestes raides) → pause immédiate",
      ]},
      demonstration: { content: 'Démo formateur (15 min) :', items: [
        "Check-list complet sur la moto + bagages",
      ]},
      guidage: { content: 'Conduite (60 min) :', items: [ "Tu planifies + tu roules + pause à 1h" ]},
      autonomie: { content: '15 min :', items: [ "Voyage retour autonome" ]},
      bilan: { content: 'Bilan :', items: [
        "« Quel signe de fatigue tu as ressenti ? »",
        "Moniteur : prochaine = C4-c (facteurs risque).",
      ]},
    },
  },

  {
    slug: 'facteurs-risque',
    competence: 'C4-c',
    competenceCat: 'C4',
    competenceLabel: 'Pratiquer une conduite autonome',
    title: 'Connaître les facteurs et situations à risque',
    kicker: 'C4-c · Salle + Circulation · 1h · Individuel',
    accent: '#16a34a',
    icon: 'AlertTriangle',
    duree: '1h',
    summary: "Identification des facteurs internes (alcool, stupéfiants, médicaments, fatigue, émotions) et externes (météo, état route).",
    meta: { duree: '1 heure', niveau: 'C4-c · Autonomie (REMC)', lieu: 'Salle + retour terrain', materiel: 'Vidéos · stats accidentologie' },
    steps: {
      evalStatique: { content: 'En salle, 15 min :', items: [
        "« Cite-moi 5 facteurs de risque pour un motard. »",
        "« À partir de combien de bières au-dessus de la limite ? »",
        "« Effets stupéfiants sur la conduite ? »",
      ]},
      evalDynamique: { content: '0 dynamique — théorie pure', items: [] },
      objectif: { content: "« À la fin, tu sauras <strong>identifier les facteurs de risque</strong> et savoir y faire face. »", notes: 'Livret REMC : C4-c. R234-1 (alcool), R235-1 (stupéfiants).' },
      criteres: { content: 'Critères :', items: [
        "Citation de 5 facteurs internes + 5 externes",
        "Connaissance des seuils légaux alcool (0,5 g/L · 0,2 g/L jeune permis)",
        "Connaissance des sanctions stupéfiants (tolérance zéro)",
      ]},
      cours: { content: '30 min cours salle :', items: [
        "Stats : 30% accidents mortels = alcool, 23% = stupéfiants (ONISR)",
        "Médicaments : pictogrammes (jaune/orange/rouge sur boîte)",
        "Fatigue + émotions : 20% des accidents",
        "Météo : pluie × 2 distance d'arrêt, vent latéral en pont/voie rapide",
      ]},
      demonstration: { content: 'Démo support :', items: [
        "Vidéo accidentologie (ONISR)",
        "Lecture pictogramme médicament",
      ]},
      guidage: { content: 'Q&R 10 min :', items: [
        "Tu cites tes facteurs personnels (fatigue matinale, stress travail, médicaments ?)",
      ]},
      autonomie: { content: '5 min :', items: [ "Identification 2 facteurs personnels" ]},
      bilan: { content: 'Bilan :', items: [
        "« Quel facteur tu sous-estimes le plus ? »",
        "Moniteur : prochaine = C4-d (accident).",
      ]},
    },
  },

  {
    slug: 'conduite-tenir-accident',
    competence: 'C4-d',
    competenceCat: 'C4',
    competenceLabel: 'Pratiquer une conduite autonome',
    title: "Conduite à tenir en cas d'accident",
    kicker: 'C4-d · Salle · 45 min · Groupe possible',
    accent: '#16a34a',
    icon: 'Siren',
    duree: '45 min',
    summary: "Protocole PAS (Protéger – Alerter – Secourir). Numéros d'urgence, protection zone, alerte précise, premiers gestes.",
    meta: { duree: '45 minutes', niveau: 'C4-d · Autonomie (REMC)', lieu: 'Salle (théorie) + simulation extérieure', materiel: 'Triangle · gilet HV · trousse premiers secours' },
    steps: {
      evalStatique: { content: 'En salle :', items: [
        "« Cite-moi le protocole PAS. »",
        "« Numéro d'urgence européen ? »",
        "« On bouge un blessé ou non ? »",
      ]},
      evalDynamique: { content: 'Simulation 5 min :', items: [
        "Protèges-tu la zone d'abord ?",
        "Alerte précise (lieu, victimes, état) ?",
      ]},
      objectif: { content: "« À la fin, tu sauras <strong>appliquer le protocole PAS</strong> face à un accident. »", notes: 'Livret REMC : C4-d. PSC1 fortement recommandé.' },
      criteres: { content: 'Critères :', items: [
        "P — Protéger : triangle ≥ 30 m, gilet HV, couper moteurs",
        "A — Alerter : 112 (européen) ou 18 (pompiers) avec lieu précis",
        "S — Secourir : ne pas bouger blessé sauf danger immédiat",
        "0 sur-accident causé",
      ]},
      cours: { content: '15 min cours salle :', items: [
        "Protocole PAS détaillé (HAS 2024)",
        "Que dire au 112 : lieu, nb victimes, état (conscient, respire), risques (feu, fuite)",
        "5 gestes interdits : enlever casque blessé, lui faire boire, le bouger, etc.",
      ]},
      demonstration: { content: 'Démo (10 min) :', items: [
        "Pose triangle + appel 112 simulé",
        "Position latérale de sécurité (PSC1)",
      ]},
      guidage: { content: 'Pratique élève (10 min) :', items: [
        "1 simulation complète protocole PAS",
      ]},
      autonomie: { content: '5 min :', items: [ "Protocole exécuté seul + Q&R" ]},
      bilan: { content: 'Bilan :', items: [
        "« Te sens-tu prêt(e) à réagir si tu arrives le 1er sur un accident ? »",
        "Moniteur : recommander PSC1 si pas encore fait.",
      ]},
    },
  },

  {
    slug: 'aides-conduite-abs',
    competence: 'C4-e',
    competenceCat: 'C4',
    competenceLabel: 'Pratiquer une conduite autonome',
    title: 'Connaître les aides à la conduite (ABS, contrôle traction)',
    kicker: 'C4-e · Salle + Plateau · 45 min · Individuel',
    accent: '#16a34a',
    icon: 'ShieldCheck',
    duree: '45 min',
    summary: "Compréhension du fonctionnement ABS, contrôle de traction, anti-wheelie. Limites et conditions d'utilisation.",
    meta: { duree: '45 minutes', niveau: 'C4-e · Autonomie (REMC)', lieu: 'Salle + plateau si moto équipée', materiel: 'Moto avec ABS' },
    steps: {
      evalStatique: { content: 'En salle :', items: [
        "« Comment fonctionne l'ABS ? »",
        "« L'ABS rend-il le freinage plus court ? »",
        "« Date obligatoire ABS sur 2RM ≥ 125 cm³ ? »",
      ]},
      evalDynamique: { content: '1 freinage urgence sol mouillé — observer :', items: [
        "Écoutes-tu le retour ABS (vibrations levier) ?",
        "Maintient-il une pression franche jusqu'à l'arrêt ?",
      ]},
      objectif: { content: "« À la fin, tu sauras <strong>utiliser efficacement les aides</strong> (ABS notamment) sans excès de confiance. »", notes: 'Livret REMC : C4-e. ABS obligatoire ≥ 125 cm³ depuis 2017.' },
      criteres: { content: 'Critères :', items: [
        "Pression franche + maintenue lors d'un freinage d'urgence",
        "Compréhension : ABS = anti-blocage, pas raccourci de distance",
        "Adaptation aux limites (ABS off-road souvent débrayable)",
      ]},
      cours: { content: '15 min cours :', items: [
        "ABS : cycles 10-20 fois/sec pour éviter blocage roue",
        "Limite : ne raccourcit PAS la distance d'arrêt (parfois +5%)",
        "Contrôle traction : limite glissement roue arrière en accélération",
      ]},
      demonstration: { content: 'Démo plateau (10 min) :', items: [
        "Freinage urgence + retour ABS commenté",
      ]},
      guidage: { content: 'Pratique élève (15 min) :', items: [
        "2-3 freinages d'urgence avec ABS actif",
      ]},
      autonomie: { content: '5 min :', items: [ "Freinage urgence solide" ]},
      bilan: { content: 'Bilan :', items: [
        "« L'ABS te donne plus de confiance ou tu y penses pas ? »",
        "Moniteur : prochaine = C4-f (entretien).",
      ]},
    },
  },

  {
    slug: 'entretien-moto',
    competence: 'C4-f',
    competenceCat: 'C4',
    competenceLabel: 'Pratiquer une conduite autonome',
    title: 'Entretien courant de la moto',
    kicker: 'C4-f · Salle + Atelier · 1h · Individuel',
    accent: '#16a34a',
    icon: 'Wrench',
    duree: '1h',
    summary: "Vérifications hebdomadaires (pneus, niveaux, éclairage, chaîne) et points-clés de l'entretien préventif.",
    meta: { duree: '1 heure', niveau: 'C4-f · Autonomie (REMC)', lieu: 'Atelier ou parking', materiel: 'Moto · manomètre · clé · graisse chaîne' },
    steps: {
      evalStatique: { content: 'Avant pratique :', items: [
        "« Combien de fois par semaine tu vérifies les pneus ? »",
        "« Quelle pression pour ta moto ? »",
      ]},
      evalDynamique: { content: 'Tu fais ta pré-check :', items: [
        "Vérifie-t-il : pneus, freins, éclairage, niveaux, chaîne ?",
      ]},
      objectif: { content: "« À la fin, tu sauras <strong>effectuer la vérification hebdomadaire</strong> de ta moto (10 min). »", notes: 'Livret REMC : C4-f.' },
      criteres: { content: 'Critères :', items: [
        "Vérification pression pneus à froid",
        "Niveaux : huile, liquide frein, liquide refroidissement",
        "Éclairage AV + AR + clignotants + stop",
        "Chaîne : tension + graissage si besoin",
      ]},
      cours: { content: '20 min atelier :', items: [
        "Pression pneus : valeur constructeur (manuel + autocollant)",
        "Niveau huile : sur béquille centrale, moteur chaud",
        "Chaîne : tension 2-3 cm de flèche, graisser tous les 500 km",
      ]},
      demonstration: { content: 'Démo formateur (15 min) :', items: [
        "Vérification complète commentée",
      ]},
      guidage: { content: 'Pratique élève (15 min) :', items: [
        "Tu refais la vérification complète",
      ]},
      autonomie: { content: '10 min :', items: [ "Vérification autonome OK + identification 1 défaut" ]},
      bilan: { content: 'Bilan :', items: [
        "« Quel point tu oubliais avant ? »",
        "Moniteur : prochaine = C4-g (éco-conduite).",
      ]},
    },
  },

  {
    slug: 'eco-conduite',
    competence: 'C4-g',
    competenceCat: 'C4',
    competenceLabel: 'Pratiquer une conduite autonome',
    title: 'Pratiquer une conduite économique et écologique',
    kicker: 'C4-g · Circulation · 45 min · Individuel',
    accent: '#16a34a',
    icon: 'Leaf',
    duree: '45 min',
    summary: "Anticipation, allure stable, régime moteur optimal, choix du rapport adapté : économie carburant et confort de conduite.",
    meta: { duree: '45 minutes', niveau: 'C4-g · Autonomie (REMC)', lieu: 'Trajet mixte ville + route', materiel: 'Casque-radio' },
    steps: {
      evalStatique: { content: 'Avant de partir :', items: [
        "« À quel régime tu changes de rapport en moyenne ? »",
        "« Pourquoi l'éco-conduite réduit la consommation ? »",
      ]},
      evalDynamique: { content: '10 min observation :', items: [
        "Anticipation visible ou freinages brusques ?",
        "Régime moteur stable ou explosif ?",
      ]},
      objectif: { content: "« À la fin, tu sauras <strong>pratiquer l'éco-conduite</strong> (allure stable, régime optimal). »", notes: 'Livret REMC : C4-g. Gain typique : 15-20% de carburant.' },
      criteres: { content: 'Critères :', items: [
        "Changement rapport supérieur dès 4000-5000 tr/min (essence)",
        "Anticipation : lever de gaz vs freinage brusque (90/10)",
        "Allure stable (limite régulateur si dispo)",
        "0 sur-régime (red zone)",
      ]},
      cours: { content: '10 min pré-briefing :', items: [
        "Régime optimal : couple max moteur (souvent 4000-5000 tr/min en essence)",
        "Effet d'aspiration interdit (poursuite trop proche dangereuse)",
        "Pression pneus optimale = consommation -3 à -5%",
      ]},
      demonstration: { content: 'Démo formateur (10 min) :', items: [
        "Conduite anticipée commentée : « ralentissement loin, allure stable »",
      ]},
      guidage: { content: 'Conduite élève (15 min) :', items: [
        "Tu annonces tes changements de rapport",
      ]},
      autonomie: { content: '5 min :', items: [ "Anticipations visibles + 0 sur-régime" ]},
      bilan: { content: 'Bilan :', items: [
        "« Ta conduite te semble plus fluide en éco ? »",
        "Moniteur : livret C1→C4 complet. Bravo !",
      ]},
    },
  },
];

export const CIRCULATION_CATEGORIES = [
  {
    id: 'C2',
    label: 'C2 · Circulation normale',
    color: '#7c3aed',
    intent: 'Règles, signalisation, allure, placement, intersections, giratoires.',
  },
  {
    id: 'C3',
    label: 'C3 · Conditions difficiles',
    color: '#dc2626',
    intent: 'Distances, dépassements, virages, météo, voie rapide, file dense, CIF, tunnels.',
  },
  {
    id: 'C4',
    label: 'C4 · Autonomie',
    color: '#16a34a',
    intent: 'Itinéraires, longue distance, risques, accident, aides, entretien, éco-conduite.',
  },
];

export function getFicheCirculationBySlug(slug) {
  return FICHES_CIRCULATION.find((f) => f.slug === slug);
}
